import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiResponse } from '../config/api.config';
import { Payment } from '../models/payment.model';
import { ApiStore, newestFirst } from './api-store';
import { PurchaseOrderService } from './purchase-order.service';

export type CreatePaymentInput = Omit<Payment, 'id'>;

/**
 * Payments to vendors (/payments). A payment can apply against several
 * purchase orders and/or expenses; the API rejects amounts above the
 * remaining balance and updates the purchase orders' payment status.
 */
@Injectable({ providedIn: 'root' })
export class PaymentService extends ApiStore<Payment> {
  protected readonly resource = 'payments';
  protected override readonly order = newestFirst<Payment>((p) => p.paymentDate);
  protected override readonly readAbility = 'read.vendorPayments' as const;
  private readonly purchaseOrderService = inject(PurchaseOrderService);

  create(input: CreatePaymentInput): Observable<Payment> {
    return this.save(
      this.http.post<ApiResponse<Payment>>(this.url, input).pipe(tap(() => this.purchaseOrderService.refresh())),
    );
  }

  /** "Undo payment" — deletes the payment, releasing its applied amounts. */
  remove(id: string): Observable<unknown> {
    this.removeLocal(id);
    return this.send(this.http.delete(`${this.url}/${id}`).pipe(tap(() => this.purchaseOrderService.refresh())));
  }
}
